import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils';
import { Digits, CodeInputVariant } from '../../types';

type BaseCodeInputAttributes = Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  'id' | 'className' | 'onFocus' | 'onClick' | 'onInput'
>;

const CodeInputVariants = cva(
  ` text-3xl text-center transition-all placeholder:text-foreground-disabled font-semibold border-2 rounded-lg focus:outline-none focus:ring-4 focus:ring-border-base`,
  {
    variants: {
      color: {
        default: 'bg-background-input',
        grey: 'bg-background-input'
      },
      hasError: {
        true: '!border-error-text focus:[box-shadow:0_0_0_4px_theme(colors.error.bg)]',
        false: '!border-border-base focus:!border-primary-base'
      },
      size: {
        sm: 'w-12 h-12',
        md: 'w-14 h-14',
        lg: 'w-16 h-16 text-4xl'
      },
      disabled: {
        true: 'bg-background-input placeholder:text-background-disabled cursor-not-allowed !border-border-base'
      }
    },
    defaultVariants: {
      color: 'default',
      size: 'md'
    }
  }
);

type CodeInputVariantProps = VariantProps<typeof CodeInputVariants>;

export interface CodeInputProps extends CodeInputVariantProps, BaseCodeInputAttributes {
  variant?: CodeInputVariant;
  onComplete?: (code: string) => void;
  onChange?: (code: string) => void;
  placeholder?: Digits;
  value?: string;
  defaultValue?: string;
  hasError?: boolean;
  disabled?: boolean;
}

export const CodeInput = React.forwardRef<HTMLDivElement, CodeInputProps>((props, ref) => {
  const {
    size,
    disabled = false,
    color,
    hasError = false,
    variant = '4Digit',
    placeholder = variant === '4Digit' ? '0000' : '000000',
    value,
    defaultValue,
    onChange,
    onComplete,
    className,
    id
  } = props;

  const VariantLengths: Record<CodeInputVariant, number> = {
    '4Digit': 4,
    '6Digit': 6
  };
  const length = VariantLengths[variant];

  React.useEffect(() => {
    const initialValues = (value as string)?.split('') || (defaultValue as string)?.split('') || [];
    const filled = Array(length)
      .fill('')
      .map((_, i) => initialValues[i] || '');
    setValues(filled);
  }, [value, defaultValue, length]);

  const [values, setValues] = React.useState<string[]>(Array(length).fill(''));
  const inputsRef = React.useRef<(HTMLInputElement | null)[]>([]);
  // The code is one tab stop: the focused box, else the first empty one, or the last once complete.
  const [focusedIndex, setFocusedIndex] = React.useState<number | null>(null);
  const firstEmpty = values.indexOf('');
  const tabStop = focusedIndex ?? (firstEmpty === -1 ? length - 1 : firstEmpty);

  const handleChange = (value: string, index: number) => {
    if (!/^[0-9]?$/.test(value)) return; // only numbers

    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);

    if (value && index < length - 1) {
      if (inputsRef.current[index + 1]?.value != '') {
        inputsRef.current[index + 2]?.focus(); // skip a line if its already filled in
      } else {
        inputsRef.current[index + 1]?.focus();
      }
    }

    if (onChange) {
      onChange(newValues.join(''));
    }
    if (newValues.every(val => val !== '')) {
      onComplete?.(newValues.join(''));
    }
  };

  //Removing
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace' && !values[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft') {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight') {
      inputsRef.current[index + 1]?.focus();
    }
  };

  return (
    <div
      ref={ref}
      className='flex gap-sm'
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget)) setFocusedIndex(null);
      }}
    >
      {values.map((v, index) => (
        <React.Fragment key={index}>
          <input
            id={index === 0 ? id : undefined}
            data-testid={`code-input-${index}`}
            ref={el => {
              inputsRef.current[index] = el;
            }}
            type='text'
            inputMode='numeric'
            placeholder={placeholder && placeholder.slice(0, length)[index]}
            maxLength={1}
            tabIndex={index === tabStop ? 0 : -1}
            onFocus={() => setFocusedIndex(index)}
            value={v}
            onChange={e => handleChange(e.target.value, index)}
            onKeyDown={e => handleKeyDown(e, index)}
            className={cn(CodeInputVariants({ size, color, hasError, disabled }), className)}
            disabled={disabled}
          />
          {length === 6 && index === 2 && (
            <p
              className={cn(
                'text-4xl mt-sm',
                disabled ? 'text-background-disabled' : 'text-foreground-disabled'
              )}
            >
              -
            </p>
          )}
        </React.Fragment>
      ))}
    </div>
  );
});

CodeInput.displayName = 'CodeInput';
